import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./CsaSignupPage.css";
import CTAHero from "../components/CTAHero";
import { SeasonContext } from "../components/dashboard/store/SeasonContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { ModalContext } from "../components/dashboard/store/ModalContext";
import CsaApplicationForm from "../components/CsaApplicationForm";
import { ApplicationPeriod } from "../api/season";
import { useAuthUser } from "../components/dashboard/hooks/useAuthUser";
import { fetchContentDocuments } from "../api/contentful";
import CsaTiersDisplay from "../components/CsaTiersDisplay";
import {
  CSA_SIGNUP_REDIRECT,
  loginPathWithRedirect,
  signupPathWithRedirect,
} from "../utils/authRedirect";
import { userHasSubscriptionHistory } from "../utils/tierPricing";
import {
  CSA_NO_HISTORY_TEXT,
  CSA_HISTORY_TEXT,
  CSA_NOT_LOGGED_IN_TEXT,
  CSA_WAITLIST_APPLICATION_PERIOD_TEXT,
  CSA_IMPORTANT_INFORMATION_TEXT,
} from "../utils/csaSignupPage.helpers";

const CsaSignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applyFromEmail = searchParams.get("apply") === "1";
  const { applicationPeriod, waitlist, joinWaitlist } =
    useContext(SeasonContext);
  const { openModal, closeModal } = useContext(ModalContext);
  const { details, isAuthenticated, userId, detailsLoading, token } =
    useAuthUser();
  const [csaInformationLeafletLink, setCsaInformationLeafletLink] =
    useState("");

  function loginPageLink() {
    closeModal();
    navigate(loginPathWithRedirect(CSA_SIGNUP_REDIRECT));
  }

  function createProfilePageLink() {
    closeModal();
    navigate(signupPathWithRedirect(CSA_SIGNUP_REDIRECT));
  }

  const hasSubscriptionHistory =
    (details?.subscriptionHistory?.length ?? 0) > 0;
  const isWaitlistedUser = Boolean(
    userId && waitlist.some((waitlistedUserId) => waitlistedUserId === userId),
  );

  async function handleNotificationRequest() {
    if (!isAuthenticated) {
      openModal({
        title: "Warning",
        body: "You must be logged in to subscribe to the notification email.",
        buttons: [
          { text: "Go to Login page", callback: () => loginPageLink() },
          { text: "Create a profile", callback: () => createProfilePageLink() },
        ],
        showCloseButton: true,
      });
      return;
    }
    if (hasSubscriptionHistory) {
      openModal({
        title: "You'll be notified!",
        body: "Since you've been a CSA member previously, you'll automatically be notified when the early application period starts.",
        showCloseButton: true,
      });
      return;
    }
    try {
      const alreadyOnWaitlist = await joinWaitlist();
      if (alreadyOnWaitlist) {
        openModal({
          title: "You're already on the waitlist",
          body: "We will notify you by email when the application period starts.",
          showCloseButton: true,
        });
        return;
      }

      openModal({
        title: "Hurray!",
        body: "You've successfully been added to the waitlist and will be notified once the application period starts!",
        showCloseButton: true,
      });
    } catch {
      openModal({
        title: "Something went wrong",
        body: "Could not add you to the waitlist. Please try again.",
        showCloseButton: true,
      });
    }
  }

  const canSignUp =
    applicationPeriod === ApplicationPeriod.FreeForAll ||
    (applicationPeriod === ApplicationPeriod.PreviousCSA &&
      hasSubscriptionHistory) ||
    (applicationPeriod === ApplicationPeriod.Waitlisted &&
      (hasSubscriptionHistory || isWaitlistedUser));

  const applicationPeriodClosed = !canSignUp;

  useEffect(() => {
    if (!applyFromEmail) return;
    if (token && detailsLoading) return;
    if (!isAuthenticated) {
      navigate(loginPathWithRedirect(CSA_SIGNUP_REDIRECT), { replace: true });
    }
  }, [applyFromEmail, token, detailsLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const loadDocuments = async () => {
      const { items: documentItems } = await fetchContentDocuments();

      const csaLeaflet = documentItems.find(
        (document) => document.fields?.title === "CSA Information Leaflet",
      );

      const leafletUrl = csaLeaflet?.fields?.file?.fields?.file?.url ?? "";
      setCsaInformationLeafletLink(leafletUrl);
    };

    loadDocuments();
  }, []);

  function getApplicationPeriodNotificationText(): string {
    if (
      isAuthenticated &&
      userHasSubscriptionHistory(details?.subscriptionHistory)
    ) {
      return CSA_HISTORY_TEXT;
    }
    if (
      isAuthenticated &&
      !userHasSubscriptionHistory(details?.subscriptionHistory) &&
      applicationPeriod !== ApplicationPeriod.Waitlisted
    ) {
      return CSA_NO_HISTORY_TEXT;
    }
    if (
      isAuthenticated &&
      !userHasSubscriptionHistory(details?.subscriptionHistory) &&
      applicationPeriod === ApplicationPeriod.Waitlisted
    ) {
      return CSA_WAITLIST_APPLICATION_PERIOD_TEXT;
    }
    return CSA_NOT_LOGGED_IN_TEXT;
  }

  return (
    <>
      <div className="content-container csa-signup-container">
        {applicationPeriodClosed && (
          <>
            <div className="wrapper-card green csa-disclaimer-box">
              <div className="info-card">
                <h3>Keep me updated for next season</h3>
                <p>
                  {getApplicationPeriodNotificationText()} Until then, feel free
                  to read more about this year's CSA below.
                </p>
                {userHasSubscriptionHistory(details?.subscriptionHistory) ? (
                  ""
                ) : isWaitlistedUser ? (
                  <button
                    className="btn "
                    onClick={handleNotificationRequest}
                    disabled={true}
                  >
                    You're already on the waitlist!
                  </button>
                ) : !userHasSubscriptionHistory(details?.subscriptionHistory) &&
                  applicationPeriod === ApplicationPeriod.Waitlisted ? (
                  " "
                ) : (
                  <button
                    className="btn csa-alert-btn"
                    onClick={handleNotificationRequest}
                  >
                    I want to be notified!
                  </button>
                )}
              </div>
            </div>
            <CTAHero
              xl={false}
              textColor={"red"}
              background={"pink"}
              title="The application period is closed"
            />
            <br />
            <div className=" tiers-and-prices-form-wrapper">
              <h2>Tiers and Prices for this season</h2>

              <div className="csa-box-tiers-container">
                <CsaTiersDisplay />
              </div>
            </div>
          </>
        )}
        {canSignUp ? (
          <CsaApplicationForm />
        ) : (
          <div className="wrapper-card green csa-disclaimer-box">
            <h3>Important information</h3>
            <p>{CSA_IMPORTANT_INFORMATION_TEXT}</p>
            {csaInformationLeafletLink && (
              <div className="leaflet-plus-btn">
                <p>
                  Please read the CSA information leaflet here{" "}
                  <FontAwesomeIcon icon={faArrowRight} color="#000000" />
                </p>{" "}
                <a
                  className="btn"
                  href={csaInformationLeafletLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read me!
                </a>
              </div>
            )}
          </div>
        )}
      </div>
      {applicationPeriodClosed && (
        <CTAHero
          textColor={"green"}
          background={"green"}
          action={handleNotificationRequest}
          actionText="Notify me when I can apply!"
          xl={false}
        />
      )}
    </>
  );
};

export default CsaSignupPage;
